import { FunctionComponent } from "preact";
import { useSignal } from "@preact/signals";
import { useEffect } from "preact/hooks";
import { Monster } from "../data/interfaces.ts";

interface MonsterCardProps {
  monster: Monster;
  onCountChange?: (monsterId: number, newCount: number) => void; // Add this prop
  isFake?: boolean;
}

export const MonsterCard: FunctionComponent<MonsterCardProps> = ({
  monster: initialMonster,
  onCountChange, // Destructure the new prop
  isFake, 
}) => {
  const isLoading = useSignal(false);
  const monster = useSignal(initialMonster);

  useEffect(() => {
    monster.value = initialMonster;
  }, [initialMonster]);

  const handleIncrement = async () => {
    if (isLoading.value) return;
    isLoading.value = true;
    const originalCount = monster.value.count;

    if(isFake) {
      const newCount = originalCount + 1;
      monster.value = { ...monster.value, count: newCount };
    isLoading.value = false;
      return; 
    }
    try {
      const response = await fetch("/api/incrementUserMonster", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ monsterId: monster.value.monsterId }),
      });
      if(response.redirected){
        globalThis.location.href = response.url;
        return;
      }
      if (response.ok) {
        const newCount = originalCount + 1;
        monster.value = { ...monster.value, count: newCount };
        // Call the callback if the monster is an archi
        if (onCountChange && monster.value.type === "archi") {
          onCountChange(monster.value.monsterId, newCount);
        }
      } else {
        console.error("Error incrementing monster:", await response.text());
      }
    } catch (error) {
      console.error("Error incrementing monster:", error);
    } finally {
      isLoading.value = false;
    }
  };

  const handleDecrement = async () => {
    if (isLoading.value || monster.value.count === 0) return;
    isLoading.value = true;
    const originalCount = monster.value.count;
    if(isFake) {
      const newCount = originalCount - 1;
      monster.value = { ...monster.value, count: newCount };
      isLoading.value = false;
      return; 
    }
    try {
      const response = await fetch("/api/decrementUserMonster", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ monsterId: monster.value.monsterId }),
      });
      if(response.redirected){
        globalThis.location.href = response.url;
        return;
      }
      if (response.ok) {
        const newCount = originalCount - 1;
        monster.value = { ...monster.value, count: newCount };
        // Call the callback if the monster is an archi
        if (onCountChange && monster.value.type === "archi") {
          onCountChange(monster.value.monsterId, newCount);
        }
      } else {
        console.error("Error decrementing monster:", await response.text());
      }
    } catch (error) {
      console.error("Error decrementing monster:", error);
    } finally {
      isLoading.value = false;
    }
  };

  return (
    <div
      class={`rounded-lg overflow-hidden box-border transition-all duration-200 min-w-[120px] sm:min-w-14 max-w-36 cursor-pointer flex flex-col border-2 ${
      monster.value.count > 0
        ? "b30 border-accent"
        : "b10 border30 hover:border-gray-500"
      }`}
    >
      <div class={`relative aspect-square w-full overflow-hidden ${
      monster.value.count > 0
        ? "border-tertiary transition-all"
        : "bg-primary transition-all"
      }`}>
      <img
        src={`/images/${monster.value.img}`}
        draggable={false}
        class={`object-cover w-full h-full transition-all select-none`}
      />
      {monster.value.type === "archi" && (
        <div class="absolute bottom-0.5 left-0.5">
        <img
          src="/icons/Archmonster.png"
          alt="Archi"
          class="w-5 h-6 select-none"
        />
        </div>
      )}
      {monster.value.type === "boss" && (
        <div class="absolute bottom-0.5 left-0.5">
        <img
          src="/icons/boss.png"
          alt="Boss"
          class="w-6 h-8 select-none"
        />
        </div>
      )}
      </div>

      <div class="p-2 flex flex-col">
      <h3
        title={monster.value.name}
        class={`h-10 flex items-center justify-center text-left t-accent ${
        monster.value.count > 0
          ? "line-through"
          : ""
        }`}
        style={{
        display: "-webkit-box",
        WebkitLineClamp: "2",
        WebkitBoxOrient: "vertical",
        textOverflow: "clip",
        }}
      >
        {monster.value.name}
      </h3>

      <div class="flex justify-center items-center gap-2 mt-2">
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleDecrement();
          }}
          disabled={isLoading.value || monster.value.count === 0}
          class={`bg-gray-500 text-white rounded-full p-1 ${
            isLoading.value || monster.value.count === 0
              ? isLoading.value
                ? "opacity-50 cursor-wait"
                : "opacity-30 cursor-not-allowed"
              : "hover:bg-gray-400"
          }`}
        >
          <svg
            class="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M6 12h12"
            />
          </svg>
        </button>

        <div class="text-white text-lg font-bold w-8 text-center flex items-center justify-center h-8 select-none"> {/* Ensure consistent height */}
          {isLoading.value
            ? (
              <div
                class="w-5 h-5 border-2 border-gray-500 border-t-yellow-500 rounded-full animate-spin"
              >
              </div>
            )
            : monster.value.count}
        </div>

        <button
          onClick={(e) => {
            e.stopPropagation();
            handleIncrement();
          }}
          disabled={isLoading.value}
          class={`bg-gray-500 text-white rounded-full p-1 ${
            isLoading.value ? "opacity-50 cursor-wait" : "hover:bg-gray-400"
          }`}
        >
          <svg
            class="w-6 h-6 text-yellow-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M12 6v12m-6-6h12"
            />
          </svg>
        </button>
      </div>
      </div>
    </div>
  );
};
